import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Upload, BarChart3, TrendingUp, TrendingDown, Download, LogOut,
  Filter, X, Maximize2, Minimize2, RefreshCw, ChevronLeft, ChevronRight, Loader2,
  Sparkles, Brain, Copy, Building2, Play, Cpu, FileText
} from 'lucide-react';
import { generateAIRecommendationPDF, generateMarketReportPDF } from '../utils/pdfReport';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, Cell, PieChart, Pie
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUSTER_COLORS = {
  sensible_alta: '#16a34a',
  sensible_media: '#3b82f6',
  sensible_baja: '#f59e0b',
  no_sensible: '#94a3b8',
  // Legacy fallback (en caso de cache vieja)
  inelastico: '#22c55e',
  unitario: '#3b82f6',
  elastico: '#f59e0b',
  super_elastico: '#ef4444'
};
const CLUSTER_LABELS = {
  sensible_alta: 'Sensible · Alta confianza (R²≥0.7)',
  sensible_media: 'Sensible · Media (R² 0.4-0.7)',
  sensible_baja: 'Sensible · Baja (R²<0.4)',
  no_sensible: 'No Sensible',
  inelastico: 'Inelastico (<0.5)',
  unitario: 'Unitario (0.5-1)',
  elastico: 'Elastico (1-1.8)',
  super_elastico: 'Super Elastico (>1.8)'
};

const ANALYSIS_LABEL_MAP = {
  producto: 'Productos',
  laboratorio: 'Laboratorios',
  clase_terapeutica: 'Clases Terapéuticas',
  grupo_canal: 'Canales de Distribución',
  provincia: 'Provincias',
};

function parseRecommendationItems(text) {
  if (!text) return [];
  const lines = text.split('\n').map(l => l.trim()).filter(l => l);
  const items = [];
  let current = null;
  const numberedRe = /^\s*(?:\*\*)?(\d+)[.)]\s*(?:\*\*)?\s*(.+?)(?:\*\*)?[:.]?\s*$/;
  const bulletRe = /^\s*[-•*]\s*(.+)$/;

  for (const line of lines) {
    const num = line.match(numberedRe);
    const bullet = line.match(bulletRe);

    if (num) {
      if (current) items.push(current);
      current = { title: num[2].replace(/\*\*/g, '').trim(), body: '' };
    } else if (bullet) {
      // Treat each top-level bullet as its own item
      const bulletText = bullet[1].replace(/\*\*/g, '').trim();
      // Use first sentence (before first . or :) as title, rest as body
      const splitMatch = bulletText.match(/^([^:.]+[:.])\s*(.*)$/);
      if (splitMatch && splitMatch[1].length < 120) {
        if (current) items.push(current);
        current = {
          title: splitMatch[1].replace(/[:.\s]+$/, '').trim(),
          body: splitMatch[2] || ''
        };
      } else {
        if (current) items.push(current);
        current = { title: bulletText.slice(0, 80), body: bulletText.length > 80 ? bulletText : '' };
      }
    } else {
      if (current) {
        current.body += (current.body ? '\n' : '') + line;
      } else {
        items.push({ title: '', body: line });
      }
    }
  }
  if (current) items.push(current);
  return items.length ? items : [{ title: '', body: text }];
}

const FocusWrapper = ({ title, children, pdfSection }) => {
  const [focused, setFocused] = useState(false);
  if (focused) {
    return (
      <div className="fixed inset-0 z-50 bg-white p-6 overflow-auto flex flex-col" data-testid="focus-mode">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 className="text-2xl font-bold text-[#2F3D8E]">{title}</h2>
          <Button variant="outline" size="sm" onClick={() => setFocused(false)}>
            <Minimize2 className="w-4 h-4 mr-1" /> Cerrar
          </Button>
        </div>
        {/* Forces any direct-child div (like h-[300px] wrappers) to fill available height */}
        <div className="flex-1 min-h-0 [&>div]:!h-full [&_.recharts-responsive-container]:!h-full">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border p-4" {...(pdfSection ? { 'data-pdf-section': pdfSection } : {})}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-sm text-[#2F3D8E]">{title}</h3>
        <Button variant="ghost" size="sm" onClick={() => setFocused(true)} title="Ampliar">
          <Maximize2 className="w-4 h-4" />
        </Button>
      </div>
      {children}
    </div>
  );
};

const MercadoPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [scatterData, setScatterData] = useState({});
  const [clustersByMonth, setClustersByMonth] = useState([]);
  const [productElasticity, setProductElasticity] = useState({ data: [], total: 0, page: 1 });
  const [groupedData, setGroupedData] = useState({});
  const [activeGroupTab, setActiveGroupTab] = useState('laboratorio');
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalyzed, setAiAnalyzed] = useState(0);
  const [aiAnalysisType, setAiAnalysisType] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const isAdmin = user && (user.role === 'mercado' || user.role === 'admin' || user.username === 'Gerencia');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) { navigate('/login'); return; }
    setUser(JSON.parse(userData));
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, filtersRes] = await Promise.all([
        axios.get(`${API}/mercado/stats`),
        axios.get(`${API}/mercado/filters`).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setFilterOptions(filtersRes.data);
      if (statsRes.data.has_data) {
        loadChartData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      const params = {};
      if (selectedCluster) params.cluster = selectedCluster;
      if (filters.laboratorio) params.laboratorio = filters.laboratorio;
      if (filters.clase) params.clase = filters.clase;
      if (filters.canal) params.canal = filters.canal;
      if (filters.provincia) params.provincia = filters.provincia;
      if (filters.producto) params.producto = filters.producto;

      const [scatterRes, clustersRes, prodRes] = await Promise.all([
        axios.get(`${API}/mercado/scatter/producto`, { params: { ...params, limit: 1000 } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/mercado/clusters/by-month`, { params }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/mercado/elasticity/products`, { params: { ...params, limit: 100 } }).catch(() => ({ data: { data: [], total: 0, page: 1 } }))
      ]);
      setScatterData(prev => ({ ...prev, producto: scatterRes.data.data }));
      setClustersByMonth(clustersRes.data.data);
      setProductElasticity({ ...prodRes.data });

      for (const group of ['laboratorio', 'clase_terapeutica', 'grupo_canal', 'provincia']) {
        axios.get(`${API}/mercado/elasticity/grouped/${group}`, { params: { ...params, limit: 50 } })
          .then(res => setGroupedData(prev => ({ ...prev, [group]: res.data.data })))
          .catch(() => {});
        axios.get(`${API}/mercado/scatter/${group}`, { params: { ...params, limit: 300 } })
          .then(res => setScatterData(prev => ({ ...prev, [group]: res.data.data })))
          .catch(() => {});
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (stats?.has_data) loadChartData();
  }, [filters, selectedCluster]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB per chunk (safe for most ingress limits)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    setUploading(true);
    setUploadProgress({ current: 0, total: totalChunks, stage: 'init' });

    try {
      // If small file (<1 MB), use single-shot upload
      if (file.size <= CHUNK_SIZE) {
        const formData = new FormData();
        formData.append('file', file);
        setUploadProgress({ current: 0, total: 1, stage: 'processing' });
        const res = await axios.post(`${API}/mercado/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000
        });
        toast.success(`Datos cargados: ${res.data.total_rows.toLocaleString()} filas, ${res.data.products_with_elasticity.toLocaleString()} elasticidades`);
        loadData();
        return;
      }

      // 1. Init session
      const initForm = new FormData();
      initForm.append('filename', file.name);
      initForm.append('total_chunks', totalChunks);
      const initRes = await axios.post(`${API}/mercado/upload/init`, initForm);
      const uploadId = initRes.data.upload_id;

      // 2. Upload chunks sequentially (progress toasts via state)
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const chunkForm = new FormData();
        chunkForm.append('upload_id', uploadId);
        chunkForm.append('chunk_index', i);
        chunkForm.append('chunk', chunk, `chunk_${i}`);

        // Retry once on transient network error
        let attempts = 0;
        while (attempts < 3) {
          try {
            await axios.post(`${API}/mercado/upload/chunk`, chunkForm, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000
            });
            break;
          } catch (err) {
            attempts++;
            if (attempts >= 3) throw err;
            await new Promise(r => setTimeout(r, 1500));
          }
        }
        setUploadProgress({ current: i + 1, total: totalChunks, stage: 'uploading' });
      }

      // 3. Complete (starts background processing, returns immediately)
      setUploadProgress({ current: totalChunks, total: totalChunks, stage: 'processing' });
      const completeForm = new FormData();
      completeForm.append('upload_id', uploadId);
      completeForm.append('total_chunks', totalChunks);
      const completeRes = await axios.post(`${API}/mercado/upload/complete`, completeForm, {
        timeout: 60000
      });

      // 4. Poll upload status until 'done' (raw saved) or 'error'
      const pollUntilDone = async () => {
        const maxAttempts = 720; // 720 * 10s = 2 hours max
        for (let i = 0; i < maxAttempts; i++) {
          const delay = i < 10 ? 3000 : 10000;
          await new Promise(r => setTimeout(r, delay));
          try {
            const statusRes = await axios.get(
              `${API}/mercado/upload/status/${uploadId}`,
              { timeout: 30000 }
            );
            const s = statusRes.data;
            setUploadProgress({
              current: totalChunks,
              total: totalChunks,
              stage: 'processing',
              substage: s.stage,
              raw_saved: s.raw_saved,
              progress: s.progress,
              current_group: s.current_group
            });
            if (s.status === 'done') return s.result;
            if (s.status === 'error') throw new Error(s.error || 'Error desconocido');
          } catch (pollErr) {
            if (pollErr.message?.startsWith('Error desconocido') || pollErr.response?.status === 404) {
              throw pollErr;
            }
            console.warn('Poll error, retrying:', pollErr.message);
          }
        }
        throw new Error('Tiempo de carga agotado (2 horas). La carga puede seguir en progreso; refresque la página en unos minutos.');
      };

      const result = await pollUntilDone();
      toast.success(`Carga completa: ${result.total_rows.toLocaleString()} filas guardadas en Mongo. Haga click en "Analizar" para calcular elasticidades.`);
      loadData();
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;
      toast.error(detail || err.message || 'Error al subir archivo');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ type: 'producto', name: '' });
  const [itemLoading, setItemLoading] = useState(false);

  const handleItemRecommendation = async () => {
    if (!itemForm.name.trim()) {
      toast.error('Ingrese el nombre del item');
      return;
    }
    setItemLoading(true);
    try {
      const formData = new FormData();
      formData.append('item_type', itemForm.type);
      formData.append('item_name', itemForm.name.trim());
      const res = await axios.post(`${API}/mercado/ai-recommendation-item`, formData, {
        timeout: 120000,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { generateItemRecommendationPDF } = await import('../utils/pdfReport');
      const filename = await generateItemRecommendationPDF({
        itemType: res.data.item_type,
        itemName: res.data.item_name,
        recommendation: res.data.recommendation,
        history: res.data.history,
        elasticities: res.data.elasticities
      });
      toast.success(`PDF descargado: ${filename}`);
      setItemModalOpen(false);
      setItemForm({ type: 'producto', name: '' });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Error al generar recomendacion');
    } finally {
      setItemLoading(false);
    }
  };

  const handleGenerateFullReport = async () => {
    setGeneratingReport(true);
    try {
      toast.info('Recopilando datos del informe...');
      const reportRes = await axios.get(`${API}/mercado/report-data`, { timeout: 60000 });

      const chartElements = {};
      document.querySelectorAll('[data-pdf-section]').forEach(el => {
        chartElements[el.getAttribute('data-pdf-section')] = el;
      });

      toast.info('Generando PDF profesional...');
      const filename = await generateMarketReportPDF({
        stats: reportRes.data.stats,
        filters,
        topData: reportRes.data.top_data,
        topSensitivity: reportRes.data.top_sensitivity,
        treeClasses: reportRes.data.tree_classes,
        chartAnalyses: reportRes.data.chart_analyses,
        lastMonthYear: reportRes.data.last_month_year,
        chartElements
      });
      toast.success(`Informe descargado: ${filename}`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || e.message || 'Error al generar informe');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    const startTime = Date.now();
    setAnalysisProgress({ substage: 'queued', elapsed: 0 });
    try {
      const res = await axios.post(`${API}/mercado/analyze`, null, { timeout: 60000 });
      const analysisId = res.data.analysis_id;
      toast.info('Análisis iniciado. El proceso puede tardar varios minutos según el tamaño de los datos.');

      // Poll until done - up to 2 hours with adaptive interval
      const maxAttempts = 720; // 720 * 10s = 2 hours
      let result = null;
      for (let i = 0; i < maxAttempts; i++) {
        // First 30s poll every 3s, then every 10s
        const delay = i < 10 ? 3000 : 10000;
        await new Promise(r => setTimeout(r, delay));
        try {
          const statusRes = await axios.get(
            `${API}/mercado/analyze/status/${analysisId}`,
            { timeout: 30000 }
          );
          const s = statusRes.data;
          setAnalysisProgress({
            substage: s.stage,
            progress: s.progress,
            current_group: s.current_group,
            current_step: s.current_step,
            elapsed: Math.round((Date.now() - startTime) / 1000)
          });
          if (s.status === 'done') { result = s.result; break; }
          if (s.status === 'error') throw new Error(s.error || 'Error desconocido');
        } catch (pollErr) {
          if (pollErr.message?.startsWith('Error desconocido') || pollErr.response?.status === 404) {
            throw pollErr;
          }
          console.warn('Poll analysis error, retrying:', pollErr.message);
        }
      }
      if (!result) throw new Error('Tiempo de análisis agotado (2 horas). El análisis sigue corriendo en el servidor; refresque la página en unos minutos.');

      toast.success(`Análisis completo: ${result.products_with_elasticity.toLocaleString()} elasticidades calculadas en ${Math.round((Date.now() - startTime) / 1000)}s`);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Error al analizar');
    } finally {
      setAnalyzing(false);
      setAnalysisProgress(null);
    }
  };

  const handleExport = async (groupBy) => {
    try {
      const res = await axios.get(`${API}/mercado/export/${groupBy}`, {
        responseType: 'blob',
        params: selectedCluster ? { cluster: selectedCluster } : {}
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `elasticidad_${groupBy}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al exportar');
    }
  };

  const loadPage = async (page) => {
    try {
      const params = { page, limit: 100 };
      if (selectedCluster) params.cluster = selectedCluster;
      if (filters.laboratorio) params.laboratorio = filters.laboratorio;
      const res = await axios.get(`${API}/mercado/elasticity/products`, { params });
      setProductElasticity({ ...res.data });
    } catch {
      toast.error('Error al cargar pagina');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const [aiObjectives, setAiObjectives] = useState([]);
  const generateAIRecommendation = async (groupBy) => {
    setAiLoading(true);
    setAiRecommendation(null);
    setAiObjectives([]);
    setAiAnalysisType(groupBy);
    const payload = { group_by: groupBy };
    // Forward all current filters to narrow down the AI's analysis scope
    if (filters.laboratorio) payload.laboratorio = filters.laboratorio;
    if (filters.clase) payload.clase_terapeutica = filters.clase;
    if (filters.canal) payload.grupo_canal = filters.canal;
    if (filters.provincia) payload.provincia = filters.provincia;
    if (filters.producto) payload.producto = filters.producto;    if (filters.laboratorio) payload.filter_value = filters.laboratorio;

    let lastError;
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const res = await axios.post(`${API}/mercado/ai-recommendations`, payload, {
          timeout: 180000
        });
        setAiRecommendation(res.data.recommendation);
        setAiAnalyzed(res.data.products_analyzed || 0);
        setAiObjectives(res.data.objectives || []);
        toast.success('Recomendaciones generadas por IA');
        setAiLoading(false);
        return;
      } catch (err) {
        lastError = err;
        const status = err.response?.status;
        // Retry only on transient gateway errors
        if ((status === 502 || status === 503 || status === 504) && attempt === 1) {
          toast.info('Reintentando conexión...');
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        break;
      }
    }
    console.error(lastError);
    toast.error(lastError?.response?.data?.detail || 'Error al generar recomendaciones. Intente nuevamente.');
    setAiLoading(false);
  };

  const clusterBadge = (cluster) => {
    const color = CLUSTER_COLORS[cluster] || '#888';
    const label = CLUSTER_LABELS[cluster] || cluster;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: color }}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2F3D8E]" />
      </div>
    );
  }

  const clusters = stats?.clusters || {};
  const totalItems = Object.values(clusters).reduce((s, v) => s + v, 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mercado-dashboard">
      {/* Header */}
      <header className="bg-[#2F3D8E] text-white px-6 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6" />
          <div>
            <h1 className="text-lg font-bold">Analytica - Mercado</h1>
            <p className="text-xs text-blue-200">Analisis de Elasticidad de Precios</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm">{user?.username}</span>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="p-4 max-w-[1600px] mx-auto">
        {/* Upload + Stats */}
        <div className="flex flex-wrap gap-3 mb-4">
          {isAdmin && (
          <div className="bg-white rounded-xl border p-4 flex items-center gap-3 flex-wrap">
            <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${uploading ? 'bg-gray-300' : 'bg-[#4C8C3B] hover:bg-[#3d7030] text-white'}`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? (
                uploadProgress?.stage === 'uploading'
                  ? `Subiendo ${uploadProgress.current}/${uploadProgress.total} partes`
                  : uploadProgress?.stage === 'processing'
                    ? (uploadProgress.substage === 'assembling' ? 'Ensamblando archivo...' :
                       uploadProgress.substage === 'detecting_format' ? 'Detectando formato...' :
                       uploadProgress.substage === 'saving_raw' ? `Guardando... ${uploadProgress.raw_saved ? uploadProgress.raw_saved.toLocaleString() : 0} filas` :
                       uploadProgress.substage === 'indexing' ? 'Creando indices...' :
                       uploadProgress.substage === 'raw_saved' ? 'Finalizando...' :
                       'Procesando...')
                    : 'Iniciando...'
              ) : 'Subir CSV'}
              <input type="file" accept=".csv" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="csv-upload" />
            </label>
            {uploading && uploadProgress?.stage === 'uploading' && uploadProgress.total > 0 && (
              <div className="flex items-center gap-2 min-w-[160px]">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#4C8C3B] transition-all"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 font-mono">
                  {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                </span>
              </div>
            )}
            {uploading && uploadProgress?.stage === 'processing' && uploadProgress?.progress > 0 && (
              <div className="flex items-center gap-2 min-w-[160px]">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#4C8C3B] to-[#7CC365] transition-all duration-500"
                    style={{ width: `${uploadProgress.progress}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 font-mono">
                  {uploadProgress.progress}%
                </span>
              </div>
            )}
            {!uploading && stats?.has_data && (
              <span className="text-xs text-gray-500">{stats.total_rows?.toLocaleString()} filas cargadas</span>
            )}
          </div>
          )}

          {/* Analyze button - only for admin and only enabled if data exists */}
          {!isAdmin && stats?.has_data && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3 flex-wrap">
              <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-900">
                Modo consulta: puede ver el dashboard, generar y descargar recomendaciones IA por agrupación o por item individual.
              </span>
              {stats?.total_elasticities > 0 && (
                <span className="text-[11px] text-blue-800 ml-auto">
                  {stats.total_elasticities.toLocaleString()} elasticidades
                  {stats.products_without_elasticity > 0 && (
                    <span
                      className="ml-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 cursor-help"
                      title="Se requieren al menos 2 meses de historia para calcular elasticidad. Estos productos solo tienen 1 mes registrado."
                    >
                      {stats.products_without_elasticity.toLocaleString()} omitidos (sin historial mensual)
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
          {isAdmin && stats?.has_data && (
            <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
              <Button
                onClick={handleAnalyze}
                disabled={analyzing || uploading}
                className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white"
                data-testid="analyze-btn"
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Cpu className="w-4 h-4 mr-2" />}
                {analyzing ? `Analizando... ${analysisProgress?.progress || 0}%` : 'Analizar Elasticidades'}
              </Button>
              {analyzing && (
                <div className="flex-1 max-w-md flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#2F3D8E] to-[#5DA5D8] transition-all duration-500"
                      style={{ width: `${analysisProgress?.progress || 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-600 whitespace-nowrap font-medium">
                    {analysisProgress?.elapsed || 0}s
                  </span>
                </div>
              )}
              {analyzing && analysisProgress?.current_step && (
                <span className="text-xs text-gray-500 italic">
                  {analysisProgress.current_step}
                </span>
              )}
              {stats?.total_elasticities > 0 && !analyzing && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="elasticity-summary">
                  <span className="text-xs text-gray-500">
                    {stats.total_elasticities.toLocaleString()} elasticidades calculadas
                  </span>
                  {stats.products_without_elasticity > 0 && (
                    <span
                      className="text-[11px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 cursor-help"
                      title="Se requieren al menos 2 meses de historia de ventas para calcular elasticidad punto/arco. Estos productos solo tienen 1 mes registrado."
                      data-testid="products-skipped-badge"
                    >
                      {stats.products_without_elasticity.toLocaleString()} omitidos (sin historial mensual)
                    </span>
                  )}
                </div>
              )}
              {stats?.has_data && stats?.total_elasticities === 0 && !analyzing && (
                <span className="text-xs text-amber-600 font-medium">
                  Datos sin analizar. Click en "Analizar" para calcular.
                </span>
              )}
              {stats?.total_elasticities > 0 && !analyzing && isAdmin && (
                <Button
                  onClick={handleGenerateFullReport}
                  disabled={generatingReport}
                  className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white ml-auto"
                  data-testid="generate-report-btn"
                >
                  {generatingReport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  {generatingReport ? 'Generando informe...' : 'Generar Informe PDF'}
                </Button>
              )}
            </div>
          )}

          {stats?.has_data && (
            <>
              <StatCard label="|β| Promedio" value={Math.abs(stats.avg_elasticity || 0).toFixed(2)} color="#2F3D8E" />
              <StatCard label="Sensibles · Alta" value={clusters.sensible_alta || 0} color={CLUSTER_COLORS.sensible_alta} onClick={() => setSelectedCluster(selectedCluster === 'sensible_alta' ? null : 'sensible_alta')} active={selectedCluster === 'sensible_alta'} />
              <StatCard label="Sensibles · Media" value={clusters.sensible_media || 0} color={CLUSTER_COLORS.sensible_media} onClick={() => setSelectedCluster(selectedCluster === 'sensible_media' ? null : 'sensible_media')} active={selectedCluster === 'sensible_media'} />
              <StatCard label="Sensibles · Baja" value={clusters.sensible_baja || 0} color={CLUSTER_COLORS.sensible_baja} onClick={() => setSelectedCluster(selectedCluster === 'sensible_baja' ? null : 'sensible_baja')} active={selectedCluster === 'sensible_baja'} />
              <StatCard label="No Sensibles" value={clusters.no_sensible || 0} color={CLUSTER_COLORS.no_sensible} onClick={() => setSelectedCluster(selectedCluster === 'no_sensible' ? null : 'no_sensible')} active={selectedCluster === 'no_sensible'} />
            </>
          )}
        </div>

        {!stats?.has_data ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Upload className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-bold text-gray-600 mb-2">Sin datos de mercado</h2>
            <p className="text-gray-400">Suba un archivo CSV para comenzar el analisis de elasticidad</p>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl border p-3 mb-4 flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                className="text-sm border rounded px-2 py-1.5 min-w-[180px] max-w-[240px] truncate"
                value={filters.laboratorio || ''}
                onChange={e => setFilters(f => ({ ...f, laboratorio: e.target.value || undefined }))}
                title={filters.laboratorio || 'Laboratorio'}
              >
                <option value="">Todos los Laboratorios</option>
                {(filterOptions.laboratorios || []).map(l => <option key={l} value={l} title={l}>{l}</option>)}
              </select>
              <select
                className="text-sm border rounded px-2 py-1.5 min-w-[200px] max-w-[260px] truncate"
                value={filters.clase || ''}
                onChange={e => setFilters(f => ({ ...f, clase: e.target.value || undefined }))}
                title={filters.clase || 'Clase Terapéutica'}
              >
                <option value="">Todas las Clases Terapéuticas</option>
                {(filterOptions.clases_terapeuticas || []).map(c => <option key={c} value={c} title={c}>{c}</option>)}
              </select>
              <select
                className="text-sm border rounded px-2 py-1.5 min-w-[140px]"
                value={filters.canal || ''}
                onChange={e => setFilters(f => ({ ...f, canal: e.target.value || undefined }))}
              >
                <option value="">Todos los Canales</option>
                {(filterOptions.canales || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                className="text-sm border rounded px-2 py-1.5 min-w-[140px]"
                value={filters.provincia || ''}
                onChange={e => setFilters(f => ({ ...f, provincia: e.target.value || undefined }))}
              >
                <option value="">Todas las Provincias</option>
                {(filterOptions.provincias || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <input
                type="text"
                list="productos-list"
                placeholder="Producto (escriba para buscar)..."
                className="text-sm border rounded px-2 py-1.5 min-w-[220px] max-w-[320px]"
                value={filters.producto || ''}
                onChange={e => setFilters(f => ({ ...f, producto: e.target.value || undefined }))}
                title={filters.producto || 'Producto'}
              />
              <datalist id="productos-list">
                {(filterOptions.productos || []).slice(0, 500).map(p => <option key={p} value={p} />)}
              </datalist>
              {(filters.laboratorio || filters.clase || filters.canal || filters.provincia || filters.producto || selectedCluster) && (
                <Button variant="ghost" size="sm" onClick={() => { setFilters({}); setSelectedCluster(null); }}>
                  <X className="w-3 h-3 mr-1" /> Limpiar
                </Button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 bg-white rounded-xl border p-1">
              {[
                { id: 'overview', label: 'Resumen' },
                { id: 'products', label: 'Productos' },
                { id: 'groups', label: 'Agrupaciones' },
                { id: 'historical', label: 'Historico' },
                { id: 'ai', label: 'IA Recomendaciones', icon: true },
                ...(isAdmin ? [{ id: 'admin', label: 'Administración' }] : [])
              ].map(tab => (
                <button key={tab.id}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === tab.id ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}>
                  {tab.icon && <Sparkles className="w-3.5 h-3.5" />}
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <FocusWrapper title="Dispersion: Precio vs Cantidad (Productos)" pdfSection="Dispersión Precio vs Cantidad">
                  <div className="h-80">
                    <ResponsiveContainer>
                      <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="precio_unitario" name="Precio" type="number" tick={{ fontSize: 10 }} label={{ value: 'Precio Unitario ($)', position: 'bottom', fontSize: 11 }} />
                        <YAxis dataKey="unidades" name="Unidades" type="number" tick={{ fontSize: 10 }} label={{ value: 'Unidades', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        {Object.keys(CLUSTER_COLORS).map(cluster => (
                          <Scatter key={cluster} name={CLUSTER_LABELS[cluster]} data={(scatterData.producto || []).filter(d => d.cluster === cluster)} fill={CLUSTER_COLORS[cluster]} fillOpacity={0.6} r={3} />
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                </FocusWrapper>

                <FocusWrapper title="Items por Cluster por Mes" pdfSection="Items por Cluster por Mes">
                  <div className="h-80">
                    <ResponsiveContainer>
                      <BarChart data={clustersByMonth} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="sensible_alta" name="Sensible · Alta" fill={CLUSTER_COLORS.sensible_alta} stackId="a" />
                        <Bar dataKey="sensible_media" name="Sensible · Media" fill={CLUSTER_COLORS.sensible_media} stackId="a" />
                        <Bar dataKey="sensible_baja" name="Sensible · Baja" fill={CLUSTER_COLORS.sensible_baja} stackId="a" />
                        <Bar dataKey="no_sensible" name="No Sensible" fill={CLUSTER_COLORS.no_sensible} stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </FocusWrapper>

                <FocusWrapper title="Distribucion de Clusters" pdfSection="Distribución de Clusters">
                  <div className="h-80 flex items-center justify-center">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={Object.entries(clusters).filter(([,v]) => v > 0).map(([k, v]) => ({ name: CLUSTER_LABELS[k], value: v, fill: CLUSTER_COLORS[k] }))}
                          dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={true}>
                          {Object.entries(clusters).filter(([,v]) => v > 0).map(([k]) => (
                            <Cell key={k} fill={CLUSTER_COLORS[k]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </FocusWrapper>

                <FocusWrapper title="Top 10 Productos Mas Elasticos">
                  <div className="h-80 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Producto</th>
                          <th className="text-right p-2">Elasticidad</th>
                          <th className="text-center p-2">Cluster</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(productElasticity.data || []).slice(0, 10).map((p, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="p-2 truncate max-w-[200px]">{p.Producto}</td>
                            <td className="p-2 text-right font-mono">{Math.abs(p.elasticidad || 0).toFixed(3)}</td>
                            <td className="p-2 text-center">{clusterBadge(p.cluster)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </FocusWrapper>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="bg-white rounded-xl border">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="font-semibold text-[#2F3D8E]">Elasticidad por Producto ({productElasticity.total?.toLocaleString()} resultados)</h3>
                  <Button variant="outline" size="sm" onClick={() => handleExport('producto')}>
                    <Download className="w-4 h-4 mr-1" /> Exportar CSV
                  </Button>
                </div>
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="product-table">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Producto</th>
                        <th className="text-left p-2">Laboratorio</th>
                        <th className="text-left p-2">Clase</th>
                        <th className="text-left p-2">Canal</th>
                        <th className="text-right p-2">Meses</th>
                        <th className="text-right p-2">Precio Prom.</th>
                        <th className="text-right p-2">|β|</th>
                        <th className="text-right p-2">p-value</th>
                        <th className="text-right p-2">R²</th>
                        <th className="text-center p-2">Clasificacion</th>
                        <th className="text-left p-2">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(productElasticity.data || []).map((p, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="p-2 truncate max-w-[200px]" title={p.Producto}>{p.Producto}</td>
                          <td className="p-2 truncate max-w-[120px]">{p.Laboratorio}</td>
                          <td className="p-2 truncate max-w-[120px]">{p['Clase Terapeuta I']}</td>
                          <td className="p-2 truncate max-w-[100px]">{p.GrupoCanal}</td>
                          <td className="p-2 text-right font-mono">{p.n_months ?? '-'}</td>
                          <td className="p-2 text-right font-mono">{p.avg_precio != null ? `$${Number(p.avg_precio).toFixed(2)}` : '-'}</td>
                          <td className="p-2 text-right font-mono font-bold">{p.elasticidad_abs != null ? Number(p.elasticidad_abs).toFixed(3) : '-'}</td>
                          <td className="p-2 text-right font-mono">{p.pvalue != null ? Number(p.pvalue).toFixed(3) : '-'}</td>
                          <td className="p-2 text-right font-mono">{p.r_squared != null ? Number(p.r_squared).toFixed(2) : '-'}</td>
                          <td className="p-2 text-center">{clusterBadge(p.cluster)}</td>
                          <td className="p-2 truncate max-w-[180px] text-gray-500" title={`${p.reason || ''} ${p.reason_detail || ''}`}>
                            {p.classification === 'Sensible' ? '—' : (p.reason || '').replace(/_/g, ' ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between p-3 border-t">
                  <span className="text-xs text-gray-500">Pagina {productElasticity.page} de {Math.ceil((productElasticity.total || 0) / 100)}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={productElasticity.page <= 1} onClick={() => loadPage(productElasticity.page - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={productElasticity.page * 100 >= (productElasticity.total || 0)} onClick={() => loadPage(productElasticity.page + 1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'groups' && (
              <div className="space-y-4">
                <div className="flex gap-1 bg-white rounded-xl border p-1">
                  {[
                    { id: 'laboratorio', label: 'Laboratorio' },
                    { id: 'clase_terapeutica', label: 'Clase Terapeutica' },
                    { id: 'grupo_canal', label: 'Canal' },
                    { id: 'provincia', label: 'Provincia' }
                  ].map(g => (
                    <button key={g.id}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeGroupTab === g.id ? 'bg-[#4C8C3B] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      onClick={() => setActiveGroupTab(g.id)}>
                      {g.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <FocusWrapper title={`Dispersion: Precio vs Cantidad por ${activeGroupTab}`}>
                    <div className="h-80">
                      <ResponsiveContainer>
                        <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="precio_unitario" name="Precio" type="number" tick={{ fontSize: 10 }} label={{ value: 'Precio ($)', position: 'bottom', fontSize: 11 }} />
                          <YAxis dataKey="unidades" name="Unidades" type="number" tick={{ fontSize: 10 }} label={{ value: 'Unidades', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          {Object.keys(CLUSTER_COLORS).map(cluster => (
                            <Scatter key={cluster} name={CLUSTER_LABELS[cluster]} data={(scatterData[activeGroupTab] || []).filter(d => d.cluster === cluster)} fill={CLUSTER_COLORS[cluster]} fillOpacity={0.6} r={4} />
                          ))}
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  </FocusWrapper>

                  <div className="bg-white rounded-xl border">
                    <div className="flex items-center justify-between p-3 border-b">
                      <h3 className="font-semibold text-sm text-[#2F3D8E]">Tabla: {activeGroupTab}</h3>
                      <Button variant="outline" size="sm" onClick={() => handleExport(activeGroupTab)}>
                        <Download className="w-4 h-4 mr-1" /> CSV
                      </Button>
                    </div>
                    <div className="max-h-80 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Nombre</th>
                            <th className="text-right p-2">Precio</th>
                            <th className="text-right p-2">Unidades</th>
                            <th className="text-right p-2">Valores</th>
                            <th className="text-right p-2">Elasticidad</th>
                            <th className="text-center p-2">Cluster</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(groupedData[activeGroupTab] || []).map((g, i) => {
                            const nameField = Object.keys(g).find(k => !['valores', 'unidades', 'precio_unitario', 'prev_precio', 'prev_unidades', 'prev_mes', 'elasticidad', 'elasticidad_abs', 'cluster', 'grupo', 'Año', 'mes_num', 'Mes'].includes(k));
                            return (
                              <tr key={i} className="border-b hover:bg-gray-50">
                                <td className="p-2 truncate max-w-[200px]">{g[nameField]}</td>
                                <td className="p-2 text-right font-mono">${g.precio_unitario?.toFixed(2)}</td>
                                <td className="p-2 text-right font-mono">{g.unidades?.toFixed(0)}</td>
                                <td className="p-2 text-right font-mono">${g.valores?.toFixed(0)}</td>
                                <td className="p-2 text-right font-mono font-bold">{Math.abs(g.elasticidad || 0).toFixed(3)}</td>
                                <td className="p-2 text-center">{clusterBadge(g.cluster)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'historical' && (
              <HistoricalTab filters={filters} filterOptions={filterOptions} />
            )}

            {activeTab === 'ai' && (
              <div className="space-y-5" data-testid="ai-tab">
                {/* Hero header - more elegant */}
                <div className="relative overflow-hidden rounded-2xl shadow-md" style={{
                  background: 'linear-gradient(135deg, #1a2456 0%, #2F3D8E 50%, #5DA5D8 100%)'
                }}>
                  <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)',
                    backgroundSize: '40px 40px'
                  }} />
                  <div className="relative p-7 text-white">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0 ring-1 ring-white/20">
                          <Brain className="w-7 h-7" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 rounded-full bg-white/15 text-xs font-semibold tracking-wide">GPT-5.2</span>
                            <span className="px-2 py-0.5 rounded-full bg-[#4C8C3B]/40 text-xs font-medium ring-1 ring-[#4C8C3B]/40">IA</span>
                          </div>
                          <h2 className="text-2xl font-bold tracking-tight">Asistente Estratégico</h2>
                          <p className="text-sm text-blue-100 mt-1 max-w-xl leading-relaxed">
                            Análisis profundo de elasticidad de precios con recomendaciones accionables
                            por agrupación. Identifica oportunidades, optimiza márgenes y mejora la rotación.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analysis type selector - cards */}
                <div className="bg-white rounded-2xl border shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-[#1a2456] text-base">Selecciona el enfoque del análisis</h3>
                      <p className="text-xs text-gray-500 mt-0.5">La IA generará 5 recomendaciones específicas según tu elección</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                      { id: 'producto', label: 'Productos', desc: 'Por SKU individual', icon: TrendingUp, color: 'from-purple-500 to-pink-500' },
                      { id: 'laboratorio', label: 'Laboratorios', desc: 'Por marca/fabricante', icon: Building2, color: 'from-blue-500 to-cyan-500' },
                      { id: 'clase_terapeutica', label: 'Clase Terapéutica', desc: 'Por categoría médica', icon: Filter, color: 'from-emerald-500 to-teal-500' },
                      { id: 'grupo_canal', label: 'Canal', desc: 'Por punto de venta', icon: TrendingDown, color: 'from-orange-500 to-red-500' },
                      { id: 'provincia', label: 'Provincia', desc: 'Por región', icon: Filter, color: 'from-indigo-500 to-purple-500' }
                    ].map(opt => {
                      const Icon = opt.icon;
                      const isActive = aiAnalysisType === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => generateAIRecommendation(opt.id)}
                          disabled={aiLoading}
                          className={`group relative overflow-hidden rounded-xl p-4 text-left transition-all ${
                            aiLoading ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md hover:-translate-y-0.5'
                          } ${isActive ? 'ring-2 ring-[#2F3D8E] ring-offset-2' : 'border border-gray-200'}`}
                          data-testid={`ai-btn-${opt.id}`}
                        >
                          <div className={`absolute inset-0 bg-gradient-to-br ${opt.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${opt.color} flex items-center justify-center mb-2.5`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <div className="font-semibold text-sm text-gray-800">{opt.label}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                  {filters.laboratorio && (
                    <div className="mt-4 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
                      <Filter className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-xs text-blue-900">
                        Filtro activo: <strong>{filters.laboratorio}</strong> (se aplicará al análisis de productos)
                      </span>
                    </div>
                  )}
                </div>

                {/* Individual item recommendation */}
                <div className="bg-white rounded-2xl border shadow-sm p-5">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-bold text-[#1a2456] text-base">Recomendación individual por item</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Genera una recomendación específica para un producto, laboratorio, clase o canal concreto.
                      </p>
                    </div>
                    <Button
                      onClick={() => setItemModalOpen(true)}
                      className="bg-[#1a2456] hover:bg-[#2F3D8E] text-white"
                      data-testid="open-item-modal-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Generar PDF individual
                    </Button>
                  </div>
                </div>

                {/* Loading skeleton */}
                {aiLoading && (
                  <div className="bg-white rounded-2xl border shadow-sm p-12">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full border-4 border-[#2F3D8E]/10 border-t-[#2F3D8E] animate-spin" />
                        <Brain className="w-7 h-7 text-[#2F3D8E] absolute inset-0 m-auto" />
                      </div>
                      <h3 className="font-semibold text-gray-800 mt-4">Procesando con GPT-5.2...</h3>
                      <p className="text-sm text-gray-500 mt-1 max-w-md">
                        La IA está analizando elasticidades, identificando patrones y formulando
                        recomendaciones estratégicas. Esto suele tardar 20-40 segundos.
                      </p>
                    </div>
                  </div>
                )}

                {/* Result panel - elegant */}
                {aiRecommendation && !aiLoading && (
                  <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid="ai-result">
                    {/* Result header */}
                    <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-blue-50 border-b">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 text-[#4C8C3B]" />
                            <h3 className="font-bold text-[#1a2456]">Recomendaciones Estratégicas</h3>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#4C8C3B]" />
                              {aiAnalyzed} items analizados
                            </span>
                            <span className="text-gray-400">·</span>
                            <span>{ANALYSIS_LABEL_MAP[aiAnalysisType] || aiAnalysisType}</span>
                            <span className="text-gray-400">·</span>
                            <span>{new Date().toLocaleDateString('es-EC')}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(aiRecommendation);
                              toast.success('Copiado al portapapeles');
                            }}
                            data-testid="copy-ai-btn"
                          >
                            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
                          </Button>
                          <Button
                            size="sm"
                            className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white"
                            onClick={async () => {
                              try {
                                const filename = await generateAIRecommendationPDF({
                                  analysisType: aiAnalysisType,
                                  recommendation: aiRecommendation,
                                  productsAnalyzed: aiAnalyzed,
                                  filters,
                                  objectives: aiObjectives
                                });
                                toast.success(`PDF descargado: ${filename}`);
                              } catch (e) {
                                console.error(e);
                                toast.error('Error al generar PDF');
                              }
                            }}
                            data-testid="download-ai-pdf-btn"
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Result body - SmartArt-style objectives or fallback to parsed items */}
                    <div className="p-6 space-y-4">
                      {aiObjectives.length > 0 ? (
                        aiObjectives.map((obj, idx) => {
                          const colors = [
                            'from-blue-500 to-cyan-500',
                            'from-emerald-500 to-teal-500',
                            'from-orange-500 to-red-500',
                            'from-purple-500 to-pink-500',
                            'from-indigo-500 to-violet-500',
                          ];
                          const color = colors[idx % colors.length];
                          return (
                            <div key={idx} className="border rounded-xl overflow-hidden shadow-sm">
                              {/* Objective header */}
                              <div className={`bg-gradient-to-r ${color} p-4 text-white`}>
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center font-bold text-lg">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-bold text-lg leading-tight">{obj.title}</h4>
                                    {obj.summary && (
                                      <p className="text-sm text-white/90 mt-1">{obj.summary}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {/* Recommendations */}
                              <div className="p-4 bg-white space-y-2">
                                {(obj.recommendations || []).map((rec, rIdx) => (
                                  <div key={rIdx} className="flex gap-3 p-3 rounded-lg bg-gray-50 hover:bg-blue-50/40 border border-gray-100">
                                    <div className="flex-shrink-0 flex flex-col items-center gap-1 pt-0.5">
                                      <Sparkles className="w-4 h-4 text-[#4C8C3B]" />
                                      {rec.impact && (
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                          rec.impact === 'Alto' ? 'bg-red-100 text-red-700' :
                                          rec.impact === 'Medio' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {rec.impact}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-semibold text-sm text-[#1a2456] mb-1">{rec.action}</div>
                                      <p className="text-xs text-gray-700 leading-relaxed">{rec.detail}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        parseRecommendationItems(aiRecommendation).map((item, idx) => (
                          <div key={idx} className="flex gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#5DA5D8]/40 hover:bg-blue-50/30 transition-colors">
                            {item.title && (
                              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#2F3D8E] to-[#5DA5D8] text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                {idx + 1}
                              </div>
                            )}
                            <div className="flex-1">
                              {item.title && (
                                <h4 className="font-semibold text-[#1a2456] mb-1.5 leading-snug">{item.title}</h4>
                              )}
                              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{item.body}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-3 border-t bg-gray-50/60 flex items-center justify-between">
                      <span className="text-xs text-gray-500 italic">
                        Generado por GPT-5.2 · Las recomendaciones son sugerencias basadas en datos
                      </span>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!aiRecommendation && !aiLoading && (
                  <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-14">
                    <div className="text-center max-w-md mx-auto">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2F3D8E]/5 to-[#5DA5D8]/5 flex items-center justify-center mx-auto mb-4">
                        <Brain className="w-10 h-10 text-[#2F3D8E]/30" />
                      </div>
                      <h3 className="font-bold text-gray-700 mb-1.5">¿Listo para descubrir oportunidades?</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        Selecciona uno de los enfoques de análisis arriba. La IA estudiará tus
                        elasticidades y generará un informe con <strong>5 recomendaciones estratégicas</strong>
                        accionables, listas para descargar en PDF profesional.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'admin' && isAdmin && (
              <AdminTab onAfterMutation={() => loadData()} />
            )}
          </>
        )}
      </div>

      {/* Individual Item Recommendation Modal */}
      {itemModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="item-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="font-bold text-[#1a2456]">Recomendación Individual</h3>
                <p className="text-xs text-gray-500 mt-0.5">PDF descargable con análisis específico</p>
              </div>
              <button onClick={() => setItemModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de item</label>
                <select
                  value={itemForm.type}
                  onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F3D8E]"
                  data-testid="item-type-select"
                >
                  <option value="producto">Producto</option>
                  <option value="laboratorio">Laboratorio</option>
                  <option value="clase_terapeutica">Clase Terapéutica</option>
                  <option value="grupo_canal">Canal</option>
                  <option value="provincia">Provincia</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del item</label>
                <input
                  type="text"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="Ej: Megalabs Mlb, Azuay, Antibioticos..."
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F3D8E]"
                  data-testid="item-name-input"
                />
                <p className="text-xs text-gray-500 mt-1">Escriba el nombre exacto. La IA analizará su histórico y elasticidad.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setItemModalOpen(false)} disabled={itemLoading}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleItemRecommendation}
                  disabled={itemLoading || !itemForm.name.trim()}
                  className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white"
                  data-testid="item-generate-btn"
                >
                  {itemLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  {itemLoading ? 'Generando...' : 'Generar PDF'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, onClick, active }) => (
  <div onClick={onClick}
    className={`bg-white rounded-xl border p-3 min-w-[120px] transition-all ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${active ? 'ring-2 ring-offset-1' : ''}`}
    style={active ? { ringColor: color } : {}}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-xl font-bold" style={{ color }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
  </div>
);

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-2 text-xs max-w-[250px]">
      <p className="font-bold truncate">{d.Producto || d.Laboratorio || d['Clase Terapeuta I'] || d.GrupoCanal || d.Provincia}</p>
      <p>Precio: ${d.precio_unitario?.toFixed(2)}</p>
      <p>Unidades: {d.unidades?.toFixed(0)}</p>
      <p>Elasticidad: {Math.abs(d.elasticidad || 0).toFixed(3)}</p>
      <p>Cluster: {CLUSTER_LABELS[d.cluster]}</p>
    </div>
  );
};

const HistoricalTab = ({ filters, filterOptions }) => {
  const [group, setGroup] = useState('laboratorio');
  const [histData, setHistData] = useState([]);
  const [topNames, setTopNames] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [loading, setLoading] = useState(false);

  const groupLabels = {
    laboratorio: 'Laboratorio',
    clase_terapeutica: 'Clase Terapeutica',
    grupo_canal: 'Canal',
    provincia: 'Provincia'
  };

  const groupOptions = {
    laboratorio: filterOptions.laboratorios || [],
    clase_terapeutica: filterOptions.clases_terapeuticas || [],
    grupo_canal: filterOptions.canales || [],
    provincia: filterOptions.provincias || []
  };

  const CHART_COLORS = ['#2F3D8E', '#4C8C3B', '#5DA5D8', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316'];

  useEffect(() => {
    setLoading(true);
    const url = filterValue
      ? `${API}/mercado/historical/${group}?value=${encodeURIComponent(filterValue)}`
      : `${API}/mercado/historical-summary/${group}?top=10`;

    axios.get(url)
      .then(res => {
        if (filterValue) {
          setHistData(res.data.data);
          setTopNames([filterValue]);
        } else {
          setHistData(res.data.data);
          setTopNames(res.data.top_names || []);
        }
      })
      .catch(() => toast.error('Error al cargar datos historicos'))
      .finally(() => setLoading(false));
  }, [group, filterValue]);

  // Pivot data for stacked bar chart: each month has values per name
  const months = [...new Set(histData.map(d => d.mes))];
  const pivotedValues = months.map(mes => {
    const row = { mes };
    topNames.forEach(name => {
      const item = histData.find(d => d.mes === mes && d.nombre === name);
      row[name] = item ? item.valores : 0;
    });
    return row;
  });
  const pivotedUnits = months.map(mes => {
    const row = { mes };
    topNames.forEach(name => {
      const item = histData.find(d => d.mes === mes && d.nombre === name);
      row[name] = item ? item.unidades : 0;
    });
    return row;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border p-3 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-600">Agrupar por:</span>
        {Object.entries(groupLabels).map(([key, label]) => (
          <button key={key}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${group === key ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-100 border'}`}
            onClick={() => { setGroup(key); setFilterValue(''); }}>
            {label}
          </button>
        ))}
        <span className="text-gray-300">|</span>
        <span className="text-xs text-gray-500">Filtrar:</span>
        <select className="text-sm border rounded px-2 py-1 max-w-[250px]" value={filterValue} onChange={e => setFilterValue(e.target.value)}>
          <option value="">Top 10 (todos)</option>
          {(groupOptions[group] || []).slice(0, 200).map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-[#2F3D8E]" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FocusWrapper title={`Ventas ($) por ${groupLabels[group]}${filterValue ? `: ${filterValue}` : ' - Top 10'}`}>
            <div className="h-96">
              <ResponsiveContainer>
                <BarChart data={pivotedValues} margin={{ top: 10, right: 10, bottom: 40, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v, name) => [`$${Number(v).toLocaleString()}`, name]} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  {topNames.slice(0, 10).map((name, i) => (
                    <Bar key={name} dataKey={name} fill={CHART_COLORS[i % CHART_COLORS.length]} stackId="a" />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FocusWrapper>

          <FocusWrapper title={`Unidades por ${groupLabels[group]}${filterValue ? `: ${filterValue}` : ' - Top 10'}`}>
            <div className="h-96">
              <ResponsiveContainer>
                <BarChart data={pivotedUnits} margin={{ top: 10, right: 10, bottom: 40, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v, name) => [Number(v).toLocaleString(), name]} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  {topNames.slice(0, 10).map((name, i) => (
                    <Bar key={name} dataKey={name} fill={CHART_COLORS[i % CHART_COLORS.length]} stackId="a" />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FocusWrapper>

          {/* Detail table */}
          <div className="lg:col-span-2 bg-white rounded-xl border">
            <div className="p-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-sm text-[#2F3D8E]">Detalle por {groupLabels[group]}</h3>
            </div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">{groupLabels[group]}</th>
                    <th className="text-left p-2">Mes</th>
                    <th className="text-right p-2">Valores ($)</th>
                    <th className="text-right p-2">Unidades</th>
                  </tr>
                </thead>
                <tbody>
                  {histData.slice(0, 200).map((d, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2 truncate max-w-[200px]">{d.nombre}</td>
                      <td className="p-2">{d.mes} {d.año}</td>
                      <td className="p-2 text-right font-mono">${d.valores?.toLocaleString()}</td>
                      <td className="p-2 text-right font-mono">{d.unidades?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// =========================================================
// AdminTab: CRUD por mes para datos crudos de mercado
// =========================================================
const AdminTab = ({ onAfterMutation }) => {
  const [months, setMonths] = useState([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null); // {mes, año}
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [editing, setEditing] = useState(null); // row object with row_id
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {kind:'row'|'month', data}
  const [busy, setBusy] = useState(false);
  const limit = 50;

  const loadMonths = useCallback(async () => {
    setLoadingMonths(true);
    try {
      const res = await axios.get(`${API}/mercado/admin/months`);
      setMonths(res.data.months || []);
    } catch (e) {
      toast.error('Error al listar meses');
    } finally {
      setLoadingMonths(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (!selectedMonth) return;
    setRowsLoading(true);
    try {
      const res = await axios.get(`${API}/mercado/admin/rows`, {
        params: { mes: selectedMonth.mes, 'año': selectedMonth.año, page, limit, search: search || undefined }
      });
      setRows(res.data.items || []);
      setRowsTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Error al cargar filas');
    } finally {
      setRowsLoading(false);
    }
  }, [selectedMonth, page, search]);

  useEffect(() => { loadMonths(); }, [loadMonths]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const handleExport = async (m) => {
    try {
      toast.info(`Exportando ${m.mes} ${m.año}...`);
      const res = await axios.get(`${API}/mercado/admin/month/export`, {
        params: { mes: m.mes, 'año': m.año },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mercado_${m.mes}_${m.año}_backup.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch (e) {
      toast.error('Error al exportar CSV');
    }
  };

  const handleDeleteMonth = async () => {
    const m = deleteConfirm.data;
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/mercado/admin/month`, { params: { mes: m.mes, 'año': m.año } });
      const jobId = res.data.job_id;
      toast.info('Eliminacion iniciada en background...');
      setDeleteConfirm(null);
      // Poll job status (cada 2s, hasta 60s max)
      let done = false;
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const jres = await axios.get(`${API}/mercado/admin/job/${jobId}`);
          if (jres.data.status === 'done') {
            toast.success(`${(jres.data.raw_deleted || 0).toLocaleString()} filas eliminadas. Re-analice para reflejar cambios.`);
            done = true;
            break;
          }
          if (jres.data.status === 'error') {
            toast.error('Error: ' + (jres.data.error || 'desconocido'));
            done = true;
            break;
          }
        } catch (_) { /* continue */ }
      }
      if (!done) toast.warning('El job sigue procesando en background. Refresque en unos segundos.');
      if (selectedMonth && selectedMonth.mes === m.mes && selectedMonth.año === m.año) {
        setSelectedMonth(null);
        setRows([]);
      }
      await loadMonths();
      onAfterMutation && onAfterMutation();
    } catch (e) {
      toast.error('Error al iniciar eliminacion');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteRow = async () => {
    const r = deleteConfirm.data;
    setBusy(true);
    try {
      await axios.delete(`${API}/mercado/admin/row/${r.row_id}`);
      toast.success('Fila eliminada');
      setDeleteConfirm(null);
      await loadRows();
      onAfterMutation && onAfterMutation();
    } catch (e) {
      toast.error('Error al eliminar fila');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async (updated) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/mercado/admin/row/${editing.row_id}`, updated);
      toast.success('Fila actualizada. Re-analice para reflejar cambios en elasticidad.');
      setEditing(null);
      await loadRows();
      onAfterMutation && onAfterMutation();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(rowsTotal / limit));

  return (
    <div className="space-y-5" data-testid="admin-tab">
      {/* Header / info */}
      <div className="bg-gradient-to-r from-[#2F3D8E] to-[#5DA5D8] text-white rounded-xl p-5">
        <h2 className="text-xl font-bold">Administración de Datos de Mercado</h2>
        <p className="text-sm text-blue-100 mt-1">
          Modifique o elimine información por mes. Tras cada cambio se invalida el cache del dashboard y se requiere presionar <strong>Analizar Elasticidades</strong> para reflejar los efectos.
        </p>
      </div>

      {/* Tabla de meses */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-[#1a2456]">Meses cargados</h3>
          <Button variant="outline" size="sm" onClick={loadMonths} data-testid="refresh-months-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingMonths ? 'animate-spin' : ''}`} /> Recargar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="p-3 text-left">Mes / Año</th>
                <th className="p-3 text-right">Filas</th>
                <th className="p-3 text-right">Productos</th>
                <th className="p-3 text-right">Provincias</th>
                <th className="p-3 text-right">Laboratorios</th>
                <th className="p-3 text-right">Elasticidades</th>
                <th className="p-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {months.length === 0 && !loadingMonths && (
                <tr><td colSpan="7" className="p-6 text-center text-gray-400">No hay datos cargados</td></tr>
              )}
              {months.map(m => {
                const isActive = selectedMonth && selectedMonth.mes === m.mes && selectedMonth.año === m.año;
                return (
                  <tr key={`${m.mes}-${m.año}`} className={`border-t hover:bg-gray-50 ${isActive ? 'bg-blue-50' : ''}`}>
                    <td className="p-3 font-medium text-[#1a2456]">{m.mes} {m.año}</td>
                    <td className="p-3 text-right font-mono">{m.rows.toLocaleString()}</td>
                    <td className="p-3 text-right font-mono">{m.unique_products.toLocaleString()}</td>
                    <td className="p-3 text-right font-mono">{m.unique_provinces}</td>
                    <td className="p-3 text-right font-mono">{m.unique_labs}</td>
                    <td className="p-3 text-right font-mono">{m.elasticities.toLocaleString()}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => { setSelectedMonth({ mes: m.mes, año: m.año }); setPage(1); setSearch(''); setSearchInput(''); }}
                          data-testid={`view-rows-${m.mes}-${m.año}`}
                        >
                          Ver filas
                        </Button>
                        <Button
                          size="sm" variant="outline" className="text-[#4C8C3B] hover:bg-green-50"
                          onClick={() => handleExport(m)}
                          data-testid={`export-${m.mes}-${m.año}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> CSV
                        </Button>
                        <Button
                          size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200"
                          onClick={() => setDeleteConfirm({ kind: 'month', data: m })}
                          data-testid={`delete-month-${m.mes}-${m.año}`}
                        >
                          <X className="w-3.5 h-3.5 mr-1" /> Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2 bg-blue-50/40 border-t text-xs text-gray-600">
          💡 Para <strong>reemplazar</strong> un mes: descargue el CSV (backup), elimine el mes y suba el nuevo archivo desde "Subir CSV".
        </div>
      </div>

      {/* Editor de filas del mes seleccionado */}
      {selectedMonth && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="rows-editor">
          <div className="px-5 py-3 border-b flex flex-wrap items-center gap-3">
            <h3 className="font-semibold text-[#1a2456]">
              Filas de {selectedMonth.mes} {selectedMonth.año}
              <span className="ml-2 text-xs font-normal text-gray-500">({rowsTotal.toLocaleString()} filas)</span>
            </h3>
            <div className="flex items-center gap-2 ml-auto">
              <Input
                placeholder="Buscar por nombre de producto..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
                className="w-64 h-8 text-sm"
                data-testid="rows-search-input"
              />
              <Button size="sm" variant="outline" onClick={() => { setSearch(searchInput); setPage(1); }}>
                Buscar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setSelectedMonth(null); setRows([]); setSearch(''); setSearchInput(''); }}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase sticky top-0">
                <tr>
                  <th className="p-2 text-left">ID</th>
                  <th className="p-2 text-left">Producto</th>
                  <th className="p-2 text-left">Laboratorio</th>
                  <th className="p-2 text-left">Clase</th>
                  <th className="p-2 text-left">Canal</th>
                  <th className="p-2 text-left">Provincia</th>
                  <th className="p-2 text-right">Valores</th>
                  <th className="p-2 text-right">Unidades</th>
                  <th className="p-2 text-right">Precio U.</th>
                  <th className="p-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rowsLoading && (
                  <tr><td colSpan="10" className="p-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</td></tr>
                )}
                {!rowsLoading && rows.length === 0 && (
                  <tr><td colSpan="10" className="p-6 text-center text-gray-400">Sin resultados</td></tr>
                )}
                {!rowsLoading && rows.map(r => (
                  <tr key={r.row_id} className="border-t hover:bg-gray-50">
                    <td className="p-2 text-xs font-mono text-gray-500">{r.IDPRODUCTO}</td>
                    <td className="p-2 max-w-xs truncate" title={r.Producto}>{r.Producto}</td>
                    <td className="p-2 text-xs">{r.Laboratorio}</td>
                    <td className="p-2 text-xs max-w-[160px] truncate" title={r['Clase Terapeuta I']}>{r['Clase Terapeuta I']}</td>
                    <td className="p-2 text-xs">{r.GrupoCanal}</td>
                    <td className="p-2 text-xs">{r.Provincia}</td>
                    <td className="p-2 text-right font-mono text-xs">{Number(r['Valores Mercado'] || 0).toFixed(2)}</td>
                    <td className="p-2 text-right font-mono text-xs">{Number(r['Unidades Mercado'] || 0).toFixed(2)}</td>
                    <td className="p-2 text-right font-mono text-xs">{Number(r.precio_unitario || 0).toFixed(2)}</td>
                    <td className="p-2">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditing(r)} data-testid={`edit-row-${r.row_id}`}>
                          Editar
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => setDeleteConfirm({ kind: 'row', data: r })} data-testid={`delete-row-${r.row_id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginacion */}
          {rowsTotal > limit && (
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-sm">
              <span className="text-gray-600">Página {page} de {totalPages}</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal edicion */}
      {editing && (
        <EditRowModal
          row={editing}
          onClose={() => setEditing(null)}
          onSave={handleSaveEdit}
          busy={busy}
        />
      )}

      {/* Modal confirmacion eliminacion */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b">
              <h3 className="font-bold text-red-600 text-lg">Confirmar eliminación</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {deleteConfirm.kind === 'month' ? (
                <>
                  <p>Vas a eliminar <strong>todas las filas</strong> de <strong>{deleteConfirm.data.mes} {deleteConfirm.data.año}</strong>:</p>
                  <ul className="bg-red-50 border border-red-200 rounded p-3 text-red-800">
                    <li>• {deleteConfirm.data.rows.toLocaleString()} filas de market_raw</li>
                    <li>• {deleteConfirm.data.elasticities.toLocaleString()} elasticidades calculadas</li>
                  </ul>
                  <p className="text-gray-600">Esta acción <strong>no se puede deshacer</strong>. Se recomienda descargar el CSV de backup primero.</p>
                </>
              ) : (
                <>
                  <p>¿Eliminar la fila del producto <strong>{deleteConfirm.data.Producto}</strong> ({deleteConfirm.data.Provincia} · {deleteConfirm.data.Mes} {deleteConfirm.data.Año})?</p>
                  <p className="text-gray-600">Esta acción no se puede deshacer.</p>
                </>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={busy}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white"
                onClick={deleteConfirm.kind === 'month' ? handleDeleteMonth : handleDeleteRow}
                disabled={busy}
                data-testid="confirm-delete-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Sí, eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EditRowModal = ({ row, onClose, onSave, busy }) => {
  const [form, setForm] = useState({
    Producto: row.Producto || '',
    Laboratorio: row.Laboratorio || '',
    'Clase Terapeuta I': row['Clase Terapeuta I'] || '',
    GrupoCanal: row.GrupoCanal || '',
    Provincia: row.Provincia || '',
    'Valores Mercado': row['Valores Mercado'] ?? 0,
    'Unidades Mercado': row['Unidades Mercado'] ?? 0,
  });
  const precioPreview = Number(form['Unidades Mercado']) > 0
    ? (Number(form['Valores Mercado']) / Number(form['Unidades Mercado'])).toFixed(4)
    : '0.0000';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="edit-row-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-bold text-[#1a2456]">Editar fila</h3>
            <p className="text-xs text-gray-500">ID producto: {row.IDPRODUCTO} · {row.Mes} {row.Año}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Producto</label>
            <Input value={form.Producto} onChange={e => setForm({ ...form, Producto: e.target.value })} data-testid="edit-producto" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Laboratorio</label>
            <Input value={form.Laboratorio} onChange={e => setForm({ ...form, Laboratorio: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Clase Terapeuta I</label>
            <Input value={form['Clase Terapeuta I']} onChange={e => setForm({ ...form, 'Clase Terapeuta I': e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Grupo Canal</label>
            <Input value={form.GrupoCanal} onChange={e => setForm({ ...form, GrupoCanal: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Provincia</label>
            <Input value={form.Provincia} onChange={e => setForm({ ...form, Provincia: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Valores Mercado ($)</label>
            <Input type="number" step="0.01" value={form['Valores Mercado']}
              onChange={e => setForm({ ...form, 'Valores Mercado': e.target.value })} data-testid="edit-valores" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Unidades Mercado</label>
            <Input type="number" step="0.01" value={form['Unidades Mercado']}
              onChange={e => setForm({ ...form, 'Unidades Mercado': e.target.value })} data-testid="edit-unidades" />
          </div>
          <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs">
            Precio unitario calculado: <strong className="font-mono">${precioPreview}</strong>
            <span className="text-gray-500 ml-2">(se recalcula al guardar)</span>
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white"
            onClick={() => onSave({
              ...form,
              'Valores Mercado': parseFloat(form['Valores Mercado']) || 0,
              'Unidades Mercado': parseFloat(form['Unidades Mercado']) || 0,
            })}
            disabled={busy}
            data-testid="save-edit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Guardar cambios
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MercadoPage;
